import {Customer} from "../../api/model/customer";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {isActivityWithReturnRouteProps} from "./type-guards";
import {getActivityDuration} from "../getter/common";
import {getActivityPersonCount} from "../getter/activities";

/**
 * Validates if the provided customer object is null or contains empty values for
 * the 'firstName' and 'lastName' properties, which are the minimum requirements for a valid customer.
 *
 * If the customer is null, it returns null. If both 'firstName' and 'lastName'
 * are empty strings, it also returns null. Otherwise, it returns the customer object.
 *
 * @param {Customer | null} customer - The customer object to be validated.
 * @returns {Customer | null} The original customer object if valid, otherwise null.
 */
export const checkForEmptyCustomer = (customer: Customer | null): Customer | null => {
    if (customer === null) return null
    const {firstName, lastName} = customer
    if (firstName.length === 0 && lastName.length === 0) return null
    return customer
}

/**
 * Determines if the CO2 kilogram equivalent (KgE) values, rounded to two decimal places and then
 * compared in their ceiling-rounded form, differ from each other.
 *
 * This function rounds both `previousTotalCo2KgE` and `updatedTotalCo2KgE` to two decimal places
 * and compares the ceiling of these rounded values to check for any difference. Why? Floating point imprecision in JS.
 *
 * @param {number} previousTotalCo2KgE - The previous total CO2 KgE value for comparison.
 * @param {number} updatedTotalCo2KgE - The updated total CO2 KgE value for comparison.
 * @returns {boolean} - Returns true if the ceiling of the rounded values is different, false otherwise.
 */
export const isCo2KgEValueDifferent = (previousTotalCo2KgE: number, updatedTotalCo2KgE: number): boolean => {
    const roundedPreviousTotalCo2KgE = Math.round(previousTotalCo2KgE * 100) / 100;
    const roundedUpdatedTotalCo2KgE = Math.round(updatedTotalCo2KgE * 100) / 100;
    return Math.ceil(roundedPreviousTotalCo2KgE) !== Math.ceil(roundedUpdatedTotalCo2KgE)
}

/**
 * Checks if a given activity is a return activity of another activity in a given list of activities.
 *
 * This function verifies if an activity that has return route properties matches the return ID of any
 * other activities present in the provided array of activities.
 *
 * @param {AllActivityTypes} activity - The activity to check.
 * @param {AllActivityTypesArray} activities - The array of activities to search for a matching return activity.
 * @returns {boolean} True if the given activity is a return activity of another activity in the list; otherwise, false.
 */
export const isReturnActivityOfAnotherActivity = (activity: AllActivityTypes, activities: AllActivityTypesArray): boolean => {
    if (isActivityWithReturnRouteProps(activity)) {
        const {common: {id}} = activity
        return activities.some(activity => isActivityWithReturnRouteProps(activity) && activity.returnId === id)
    }
    return false
}

/**
 * Checks whether the given value is a valid integer.
 *
 * This function determines if the input is a number that satisfies two conditions:
 * 1. It is an integer, as verified by Number.isInteger.
 * 2. It is not NaN (Not a Number).
 *
 * @param {any} value - The value to be checked for being a valid integer.
 * @returns {boolean} Returns true if the value is a valid integer, otherwise false.
 */
export const isValidInteger = (value: any) => Number.isInteger(value) && !isNaN(value)

/**
 * Checks if the duration of a given activity exceeds the total duration of a journey starting from a specific day.
 *
 * @param {AllActivityTypes} activity - The activity object containing details such as type and duration.
 * @param {number} day - The day index within the journey from which the activity starts (1-based index).
 * @param {number} journeyDurationInDays - The total duration of the journey in days.
 * @returns {boolean|null} - Returns `true` if the activity duration exceeds the journey duration starting from the given day,
 *                           `false` if it does not exceed, or `null` if the activity duration is not defined.
 */
export const activityDurationExceedsJourneyDurationFromDay = (activity: AllActivityTypes, day: number, journeyDurationInDays: number) => {
    const {common: {activityType}} = activity
    const countDays = getActivityDuration(activity)
    if (countDays !== null) switch (activityType) {
        case ACTIVITY_TYPE.car_bus:
            return !((day + countDays) <= (journeyDurationInDays + 1))
        case ACTIVITY_TYPE.hotel:
            // hotels are overnight stays
            return !((day + countDays) <= journeyDurationInDays)
        case ACTIVITY_TYPE.flight:
            return false

        default:
            activityType satisfies never
    }
    return null
}

/**
 * Determines if the number of people associated with an activity exceeds the number of people in the journey.
 *
 * @param {AllActivityTypes} activity - The activity object containing details for the number of associated people.
 * @param {number} journeyPersonCount - The total number of people in the journey.
 * @returns {boolean} Returns true if the activity's associated person count exceeds the journey person count, false otherwise.
 */
export const activityPersonCountExceedsJourneyPersonCount = (activity: AllActivityTypes, journeyPersonCount: number) => {
    const activityPersonCount = getActivityPersonCount(activity)
    if (activityPersonCount === null) return true
    return activityPersonCount > journeyPersonCount
}